import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Search } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";

const SearchForm = ({ t, appointmentNumber, email, error, searching, setAppointmentNumber, setEmail, onSubmit }) => (
  <>
    <h1 className="font-serif text-2xl text-white mb-2 text-center">{t("manageAppointmentPage.title")}</h1>
    <p className="text-white/50 text-sm mb-6 text-center">{t("manageAppointmentPage.subtitle")}</p>
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <Label className="text-white/70 text-sm">{t("manageAppointmentPage.appointmentNumber")}</Label>
        <Input
          data-testid="manage-appointment-number-input"
          value={appointmentNumber}
          onChange={(e) => setAppointmentNumber(e.target.value)}
          placeholder={t("manageAppointmentPage.appointmentNumberPlaceholder")}
          className="bg-[#1A1A1A] border-white/10 text-white rounded-none mt-1.5"
          required
        />
      </div>
      <div>
        <Label className="text-white/70 text-sm">{t("manageAppointmentPage.email")}</Label>
        <Input
          type="email"
          data-testid="manage-appointment-email-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t("manageAppointmentPage.emailPlaceholder")}
          className="bg-[#1A1A1A] border-white/10 text-white rounded-none mt-1.5"
          required
        />
      </div>
      {error && <p data-testid="manage-appointment-error" className="text-red-400 text-sm">{error}</p>}
      <Button type="submit" data-testid="manage-appointment-search-button" disabled={searching} className="w-full bg-gold text-white hover:bg-gold-hover rounded-none disabled:opacity-60 gap-2">
        <Search size={16} />
        {searching ? t("manageAppointmentPage.searching") : t("manageAppointmentPage.searchButton")}
      </Button>
    </form>
  </>
);

const CancelledResult = ({ t, onReset }) => (
  <div className="text-center">
    <CheckCircle2 size={48} className="text-gold mx-auto mb-4" />
    <h1 className="font-serif text-2xl text-white mb-2">{t("manageAppointmentPage.cancelledTitle")}</h1>
    <p className="text-white/60 mb-6">{t("manageAppointmentPage.cancelledMessage")}</p>
    <button data-testid="manage-appointment-new-search" onClick={onReset} className="text-white/50 text-sm hover:text-gold underline underline-offset-4">
      {t("manageAppointmentPage.newSearch")}
    </button>
  </div>
);

const FoundBookingResult = ({ t, lang, booking, error, cancelling, onCancel, onReset }) => (
  <div>
    <h1 className="font-serif text-2xl text-white mb-4 text-center">{t("manageAppointmentPage.foundTitle")}</h1>
    <div className="text-left bg-[#1A1A1A] rounded-lg p-4 mb-6 space-y-1.5 text-sm">
      <p className="flex justify-between text-white/70"><span>{t("manageAppointmentPage.appointmentNumber")}</span><span className="text-gold font-semibold">#{booking.appointment_number}</span></p>
      <p className="flex justify-between text-white/70"><span>{t("booking.summary.customer")}</span><span className="text-white">{booking.customer_name}</span></p>
      <p className="flex justify-between text-white/70"><span>{t("booking.summary.service")}</span><span className="text-white">{lang === "nl" ? booking.service_name_nl : booking.service_name_en}</span></p>
      <p className="flex justify-between text-white/70"><span>{t("booking.summary.date")}</span><span className="text-white">{booking.date}</span></p>
      <p className="flex justify-between text-white/70"><span>{t("booking.summary.time")}</span><span className="text-white">{booking.start_time}</span></p>
    </div>
    <p className="text-white/50 text-sm mb-6 text-center">{t("manageAppointmentPage.confirmText")}</p>
    {error && <p data-testid="manage-appointment-error" className="text-red-400 text-sm mb-4 text-center">{error}</p>}
    <div className="flex flex-col gap-3">
      <Button data-testid="manage-appointment-cancel-button" onClick={onCancel} disabled={cancelling} className="w-full bg-red-500/10 border border-red-500/40 text-red-400 hover:bg-red-500/20 rounded-none disabled:opacity-60">
        {cancelling ? t("manageAppointmentPage.cancelling") : t("manageAppointmentPage.cancelButton")}
      </Button>
      <button data-testid="manage-appointment-back-button" onClick={onReset} className="text-white/50 text-sm hover:text-gold underline underline-offset-4">
        {t("manageAppointmentPage.back")}
      </button>
    </div>
  </div>
);

export default function ManageAppointment() {
  const { t, lang } = useLanguage();
  const [appointmentNumber, setAppointmentNumber] = useState("");
  const [email, setEmail] = useState("");
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState("");
  const [cancelled, setCancelled] = useState(false);
  const [searching, setSearching] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const reset = () => {
    setBooking(null);
    setCancelled(false);
    setError("");
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setSearching(true);
    setError("");
    try {
      const { data } = await api.post("/bookings/lookup", {
        appointment_number: appointmentNumber.trim(),
        customer_email: email.trim(),
      });
      setBooking(data);
      if (data.status === "cancelled") setCancelled(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSearching(false);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    setError("");
    try {
      await api.post("/bookings/cancel-by-number", {
        appointment_number: appointmentNumber.trim(),
        customer_email: email.trim(),
      });
      setCancelled(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div data-testid="manage-appointment-page" className="min-h-[70vh] flex items-center justify-center px-4 py-16">
      <div className="max-w-md w-full bg-[#141414] border border-white/10 rounded-xl p-8">
        {!booking && (
          <SearchForm t={t} appointmentNumber={appointmentNumber} email={email} error={error} searching={searching} setAppointmentNumber={setAppointmentNumber} setEmail={setEmail} onSubmit={handleSearch} />
        )}
        {booking && cancelled && <CancelledResult t={t} onReset={reset} />}
        {booking && !cancelled && (
          <FoundBookingResult t={t} lang={lang} booking={booking} error={error} cancelling={cancelling} onCancel={handleCancel} onReset={reset} />
        )}

        <div className="mt-8 text-center">
          <Link to="/" data-testid="manage-appointment-home-link" className="text-white/30 text-xs hover:text-gold underline underline-offset-4">
            {t("booking.success.backHome")}
          </Link>
        </div>
      </div>
    </div>
  );
}
